<template>
  <section>
    <h4 class="capitalize">{{ user?.userName }}</h4>
    <div class="grid grid-cols-12 gap-5">
      <p v-if="classroomName" class="col-span-5">
        {{
          $t("labels.generic", { pt: "Turma: ", en: "Classroom: " }) +
          classroomName
        }}
      </p>

      <div class="col-span-12 flex flex-col gap-5">
        <div class="flex items-center justify-between mb-4">
          <h4>
            {{ $t("stats.course.sidebar.general") }}
          </h4>

          <Button
            :label="$t('labels.generic', { pt: 'Baixar', en: 'Download' })"
            icon="pi pi-download"
            class="p-button-sm btn-primary h-9"
            :loading="btnLoading"
            @click="downloadCsv"
          />
        </div>
      </div>

      <div
        v-if="!user?.userName"
        class="col-span-12 bg-white rounded-lg py-6 shadow"
      >
        <EmptyData
          :data="!user?.userName"
          :title="emptyState.title"
          :label="emptyState.description"
          :image="emptyState.image"
          imgSize="h-40 my-10"
        />
      </div>

      <div v-else class="col-span-12">
        <div class="flex flex-col lg:!flex-row items-center gap-5">
          <Card
            v-for="(item, i) in data"
            :key="i"
            :data="item"
            :isEmpty="item?.empty"
          >
            <template #content>
              <div
                class="flex flex-wrap justify-start md:justify-between gap-3 relative"
              >
                <div class="flex justify-center flex-wrap gap-3">
                  <img
                    class="w-16 h-16 rounded-md"
                    :src="item?.icon"
                    alt="img-icon"
                  />
                  <div class="flex flex-col gap-3">
                    <p
                      class="truncate max-w-[200px] text-[14px]"
                      :title="item?.userName"
                    >
                      <strong>{{
                        $t("labels.generic", { pt: "Nome", en: "Name" })
                      }}</strong>
                      {{ item?.userName }}
                    </p>
                    <p class="text-[14px]">
                      <strong>E-mail:</strong> {{ item?.userEmail }}
                    </p>
                    <p class="text-[14px]">
                      <strong>{{
                        $t("labels.generic", {
                          pt: "Última data de acesso:",
                          en: "Last access:",
                        })
                      }}</strong>
                      {{ item?.lastAccess }}
                    </p>
                  </div>
                </div>
                <div
                  :class="`${item?.studentStatus?.class} h-fit absolute top-0 right-0 py-1 px-2 rounded-md text-xs w-100`"
                >
                  <p class="text-[11px]">{{ item?.studentStatus?.name }}</p>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { GeneralInfoIndividualStudent } from "@/models/stats.model";
import saveAs from "file-saver";
import { defineAsyncComponent, defineComponent } from "vue";
import { GeneralService } from "./general.service";

const Card = defineAsyncComponent(() => import("../../Card/card.vue"));
export default defineComponent({
  name: "generalInfoStudent",
  components: { Card },
  computed: {
    statsService(): GeneralService {
      return new GeneralService();
    },
    classroomId(): string {
      return JSON.parse(localStorage.getItem("stdClassroomId") as string);
    },
    classroomName(): string {
      return JSON.parse(localStorage.getItem("stdClassroomName") as string);
    },
  },
  props: {
    courseId: {
      type: String,
      default: "",
    },
    studentId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      data: [] as any[],
      user: {} as GeneralInfoIndividualStudent,
      btnLoading: false as boolean,
      csv: new GeneralInfoIndividualStudent(),
      emptyState: {
        image: "empty-crawler.svg",
        title: this.$t("labels.generic", { pt: "Sem dados", en: "No data" }),
        description: this.$t("labels.generic", {
          pt: "Ainda não há dados suficientes",
          en: "No data available",
        }),
      },
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    getUser(): void {
      this.statsService.general.subscribe({
        next: (response) => {
          this.user = response;
          if (!response?.userId) return;

          this.data.push({
            ...response,
            empty: true,
            icon: this.user.photoProfile ?? require("@/assets/images/user.svg"),
          });

          this.getQuiz();
          this.getDetection();
        },
      });
      this.statsService.getStudentFromCourseById(this.courseId, this.studentId);
    },
    getQuiz(): void {
      let item = {
        title: this.user.averageQuiz,
        info: "Média geral dos quizzes",
        icon: require("@/assets/images/stats/media-icon.svg"),
        empty: false,
      };
      this.data.push(item);
    },
    getDetection(): void {
      let item = {
        title: this.user.totalsAlertedAttention,
        info: "Quantidade de alertas de detecção",
        icon: require("@/assets/images/stats/alert-icon.svg"),
        empty: false,
      };
      this.data.push(item);
    },

    downloadCsv(): void {
      this.btnLoading = true;
      delete this.csv.userId;
      const data = {
        userName: this.user.userName,
        userEmail: this.user.userEmail,
        lastAccess: this.user.lastAccess,
        averageQuiz: this.user.averageQuiz,
        studentStatus: this.user.studentStatus?.name,
        totalsAlertedAttention: this.user.totalsAlertedAttention,
      };
      const colNames = [
        "Nome",
        "E-mail",
        "Último acesso",
        "Média nos Quizzes",
        "Status",
        "Total de alertas",
      ];
      var csv = "";
      var arr = [colNames, { ...data }];
      for (var row of arr) {
        for (var prop in row) {
          var cellVal = row[prop];
          csv += '"' + cellVal + '", ';
        }
        csv += "\n";
      }

      setTimeout(() => {
        this.btnLoading = false;
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        saveAs(url, `student_general.csv`);
      }, 1000);
    },
  },
});
</script>
